import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Calendar, User, Building, Mail, AlertCircle, FileText, Image, ArrowRight } from 'lucide-react';
import axios from 'axios';
import AdminNavigation from '../components/AdminNavigation';
import SubmissionAnalytics from '../components/SubmissionAnalytics';
import AssignmentDistributionChart from '../components/AssignmentDistributionChart';
import UserActivityChart from '../components/UserActivityChart';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboardState, setDashboardState] = useState({
    loading: true,
    error: null,
    recentSubmissions: [],
    totalSubmissions: 0
  });

  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null
  });

  const [easterEggState, setEasterEggState] = useState({
    totalSubmissions: false,
    recentSubmissions: false
  });

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    try {
      // Check authentication status
      const authResponse = await axios.get('/api/auth/status');
      
      if (!authResponse.data.isAuthenticated) {
        navigate('/admin');
        return;
      }
      
      setAuthState({
        isAuthenticated: true,
        user: authResponse.data.user
      });
      
      // Load submissions data
      await loadSubmissions();
      
    } catch (error) {
      console.error('Auth/data loading error:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      } else {
        setDashboardState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load data. Please refresh the page.'
        }));
      }
    }
  };

  const loadSubmissions = async () => {
    setDashboardState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await axios.get('/api/submissions', {
        params: {
          page: 1,
          limit: 10 // Only load recent 10 submissions for overview
        }
      });
      
      setDashboardState(prev => ({
        ...prev,
        loading: false,
        recentSubmissions: response.data.submissions,
        totalSubmissions: response.data.pagination.total
      }));
      
    } catch (error) {
      console.error('Load submissions error:', error);
      setDashboardState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load dashboard data'
      }));
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation even if logout request fails
      navigate('/admin');
    }
  };

  const handlePageChange = (newPage) => {
    setDashboardState(prev => ({
      ...prev,
      pagination: { ...prev.pagination, page: newPage }
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'Not provided';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const formatAssignedTo = (assignedTo) => {
    if (!assignedTo) return 'Unassigned';
    if (assignedTo.firstName && assignedTo.lastName) {
      return `${assignedTo.firstName} ${assignedTo.lastName}`;
    }
    return assignedTo.username;
  };

  // Easter egg handlers
  const handleTotalSubmissionsDoubleClick = () => {
    setEasterEggState(prev => ({ 
      ...prev, 
      totalSubmissions: !prev.totalSubmissions 
    }));
  };

  const handleRecentSubmissionsDoubleClick = () => {
    setEasterEggState(prev => ({ 
      ...prev, 
      recentSubmissions: !prev.recentSubmissions 
    }));
  };

  if (dashboardState.loading && dashboardState.recentSubmissions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <AdminNavigation user={authState.user} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Dashboard Overview</h2>
          <p className="mt-2 text-sm text-gray-600">
            Get an overview of your form submissions, analytics, and recent activity.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0" onDoubleClick={handleTotalSubmissionsDoubleClick}>
                {easterEggState.totalSubmissions ? (
                  <img 
                    src="/TheHawk.jpg" 
                    alt="The Hawk" 
                    className="h-8 w-8 object-cover rounded-sm cursor-pointer" 
                  />
                ) : (
                  <Mail className="h-8 w-8 text-blue-600 cursor-pointer" />
                )}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Submissions</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardState.totalSubmissions}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0" onDoubleClick={handleRecentSubmissionsDoubleClick}>
                {easterEggState.recentSubmissions ? (
                  <img 
                    src="/TheHawk.jpg" 
                    alt="The Hawk" 
                    className="h-8 w-8 object-cover rounded-sm cursor-pointer" 
                  />
                ) : (
                  <Calendar className="h-8 w-8 text-green-600 cursor-pointer" />
                )}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Recent Submissions</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardState.recentSubmissions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Manage All</p>
                  <p className="text-sm text-gray-600">View all submissions</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/submissions')}
                className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
              >
                View All
                <ArrowRight className="ml-1 w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Section */}
        <SubmissionAnalytics />

        {/* Assignment & User Analytics Section */}
        <div className="space-y-6 mb-8">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Assignment & User Analytics</h3>
            <p className="text-sm text-gray-600 mb-6">
              Track submission assignments and user activity patterns over time.
            </p>
          </div>
          
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AssignmentDistributionChart />
            <UserActivityChart />
          </div>
        </div>

        {/* Error Message */}
        {dashboardState.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{dashboardState.error}</p>
                <button
                  onClick={() => loadSubmissions()}
                  className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recent Submissions Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Recent Submissions
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Latest 10 form submissions for quick overview.
                </p>
              </div>
              <button
                onClick={() => navigate('/admin/submissions')}
                className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
              >
                View All Submissions
                <ArrowRight className="ml-1 w-4 h-4" />
              </button>
            </div>
          </div>
          
          {dashboardState.recentSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions</h3>
              <p className="mt-1 text-sm text-gray-500">
                No form submissions have been received yet.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardState.recentSubmissions.map((submission) => (
                    <tr 
                      key={submission.id} 
                      className="cursor-pointer transition-colors duration-150 hover:bg-gray-50"
                      onClick={(e) => {
                        // Don't navigate if clicking on action buttons
                        if (!e.target.closest('button')) {
                          navigate(`/admin/submissions/${submission.id}`);
                        }
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{submission.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {truncateText(submission.company, 30)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {truncateText(submission.contact_person, 20)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {submission.assigned_to ? (
                            <div className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                              <User className="h-3 w-3 mr-1" />
                              {formatAssignedTo(submission.assigned_to)}
                            </div>
                          ) : (
                            <div className="flex items-center px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                              <span className="h-3 w-3 mr-1 rounded-full bg-gray-400"></span>
                              Unassigned
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(submission.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/submissions/${submission.id}`)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
